import { Button, notification, PageHeader, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { saveSampleReceivedDetailsSaga } from "./sagas";
import { sliceKey, reducer, actions } from "./slice";
import { actions as orderListActions } from "../OrderListing/slice";
import { useEffect, useState, useCallback } from "react";
import * as selectors from "./selectors";
import AddLabSampleReceptionForm from "../../../../forms/AddLabSampleReceptionForm";
import AddLabLabellingForm from "../../../../forms/AddLabLabellingForm";
import provideLabSampleReceptionSchema from "../../../../schemas/addLabSampleReceptionSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { dateTimeToString, dateToString } from "../../../../utils/formatter";
import acceptanceFormSchema from "../../../../schemas/acceptanceFormSchema";
import AcceptanceFormSuccessModal from "./components/AcceptanceFormSuccessModal";
import LabellingFormSuccessModal from "./components/LabellingFormSuccessModal";
import { MdKeyboardBackspace } from "react-icons/md";
import { reverse } from "named-urls";
import routes from "../../../../../routes";

const SampleReceived = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: saveSampleReceivedDetailsSaga });

  const dispatch = useDispatch();
  const history = useHistory();

  const { orderId } = useParams();
  const id = orderId;

  // states
  const [openAcceptanceFormSuccessModal, setOpenAcceptanceFormSuccessModal] =
    useState(false);
  const [openLabellingFormSuccessModal, setOpenLabellingFormSuccessModal] =
    useState(false);

  useEffect(() => {
    dispatch(
      actions.fetchOrderDetails({
        id,
        onSuccess: (id) => {
          dispatch(actions.fetchSampleDetails(id));
        },
        onFailure: () => {},
      })
    );
    dispatch(actions.fetchDropdown(["orderStatus"]));
    return () => {
      dispatch(actions.resetState());
    };
  }, [dispatch, id]);

  const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);
  const orderDetails = useSelector(selectors.selectOrderDetails) || null;

  const orderStatusValue = orderDetails?.status?.value || null;

  const dropdowns = useSelector(selectors.selectDropdowns);
  const loadingSampleDetails = useSelector(
    selectors.selectLoadingSampleDetails
  );
  const sampleDetails = useSelector(selectors.selectSampleDetails) || null;
  const { orderStatus = [] } = dropdowns;

  const [isSavingAcceptanceForm, setIsSavingAcceptanceForm] = useState(false);
  const [isSavingLabellingForm, setIsSavingLabellingForm] = useState(false);
  let disableFields = {
    fssaiLogoPresent: orderDetails?.sampleVariant === "Loose" ? true : false,
    fssaiLicenseNumber: orderDetails?.sampleVariant === "Loose" ? true : false,
    vegSymbolPresent: orderDetails?.sampleVariant === "Loose" ? true : false,
    jaivikKisanLogoPresent:
      orderDetails?.sampleVariant !== "Organic" ? true : false,
    brandName: orderDetails?.sampleVariant === "Loose" ? true : false,
    manufacturingDate: orderDetails?.sampleVariant === "Loose" ? true : false,
    lotNumber: orderDetails?.sampleVariant === "Loose" ? true : false,
    vegOrNonVeg: orderDetails?.sampleVariant === "Loose" ? true : false,
    expireByDate: orderDetails?.sampleVariant === "Loose" ? true : false,
    fssaiLicenseNumberPresent:
      orderDetails?.sampleVariant === "Loose" ? true : false,
    countryOfOriginPresent:
      orderDetails?.sampleVariant === "Loose" ? true : false,
    manufacturingAddressPresent:
      orderDetails?.sampleVariant === "Loose" ? true : false,
  };

  const provideDefaultValuesAcceptanceForm = useCallback(() => {
    if (sampleDetails) {
      return {
        samplePhysicalCondition: sampleDetails.samplePhysicalCondition,
        sampleSealNumber: sampleDetails.sampleSealNumber,
        conditionOfSeal: sampleDetails.conditionOfSeal,
        additionalSampleNeeded: sampleDetails.additionalSampleNeeded,
        additionalSampleReasons: sampleDetails.additionalSampleReasons,
        sampleRegistrationNumber: sampleDetails.sampleRegistrationNumber,
        expectedDateOfCompletion:
          sampleDetails?.expectedDateOfCompletion &&
          sampleDetails?.expectedDateOfCompletion.toLowerCase() !==
            "Invalid date".toLowerCase()
            ? moment(sampleDetails?.expectedDateOfCompletion, [
                "DD-MM-YYYY",
                moment.ISO_8601,
                "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
              ])
            : "",
        sampleReceivedDate:
          sampleDetails?.sampleReceivedDate &&
          sampleDetails?.sampleReceivedDate.toLowerCase() !==
            "Invalid date".toLowerCase()
            ? moment(sampleDetails?.sampleReceivedDate, [
                "DD-MM-YYYY HH:mm:ss",
                "DD-MM-YYYY HH:mm",
                "DD-MM-YYYY",
                moment.ISO_8601,
                "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
              ])
            : "",
      };
    }
    return {
      sampleReceivedDate: "",
      samplePhysicalCondition: "",
      sampleSealNumber: "",
      conditionOfSeal: "",
      additionalSampleNeeded: "",
      additionalSampleReasons: "",
      sampleRegistrationNumber: "",
      expectedDateOfCompletion: "",
    };
  }, [sampleDetails]);

  const provideDefaultValues = useCallback(
    (variant) => {
      let data = {};
      if (sampleDetails) {
        data = {
          ...sampleDetails,
          //this is because there are some orders against which Invalid date was saved
          manufacturingDate:
            sampleDetails?.manufacturingDate &&
            sampleDetails?.manufacturingDate.toLowerCase() !==
              "Invalid date".toLowerCase()
              ? moment(sampleDetails?.manufacturingDate, [
                  "DD-MM-YYYY",
                  moment.ISO_8601,
                  "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
                ])
              : "",
          expireByDate:
            sampleDetails?.expireByDate &&
            sampleDetails?.expireByDate.toLowerCase() !==
              "Invalid date".toLowerCase()
              ? moment(sampleDetails?.expireByDate, [
                  "DD-MM-YYYY",
                  moment.ISO_8601,
                  "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
                ])
              : "",
          frontImage: sampleDetails?.labelingPhotos?.[0]
            ? [
                {
                  name: "frontImage",
                  url: sampleDetails?.labelingPhotos[0],
                },
              ]
            : [],
          backImage: sampleDetails?.labelingPhotos?.[1]
            ? [
                {
                  name: "backImage",
                  url: sampleDetails?.labelingPhotos[1],
                },
              ]
            : [],
        };
      } else {
        let commonDataToSave = {
          countryOfOriginPresent: "",
          countryOfOrigin: "",
          manufacturingAddress: "",
          manufacturingAddressPresent: "",
          fssaiLicenseNumberPresent: "",
          fssaiLicenseNumber: "",
          frontImage: [],
          backImage: [],
        };
        data = commonDataToSave;

        if (variant !== "Loose") {
          data = {
            ...data,
            fssaiLogoPresent: "",
            brandName: "",
            brandNamePresent: "",
            manufacturingDate: "",
            manufacturingDatePresent: "",
            lotNumber: "",
            lotNumberPresent: "",
            vegSymbolPresent: "",
            vegOrNonVeg: "",
            expireByDate: "",
            expireByDatePresent: "",
          };
        }
        if (variant === "Organic") data.jaivikKisanLogoPresent = "";
      }

      return data;
    },
    [sampleDetails]
  );

  const getOrderStatusId = (value) => {
    const status = orderStatus.find((o) => o.value === value);
    if (status) return status._id;
    return null;
  };

  const [schema, setSchema] = useState(null);

  useEffect(() => {
    if (orderDetails) {
      setSchema(provideLabSampleReceptionSchema(orderDetails.sampleVariant));
    }
  }, [orderDetails, setSchema]);

  const {
    control: acceptanceFormControl,
    handleSubmit: handleSubmitAcceptanceForm,
    watch: watchAcceptanceForm,
    formState: { errors: acceptanceFormErrors },
    setValue: acceptanceFormSetValue,
    reset: resetAcceptanceForm,
  } = useForm({
    defaultValues: provideDefaultValuesAcceptanceForm(),
    resolver: yupResolver(acceptanceFormSchema),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: provideDefaultValues(orderDetails?.sampleVariant),
    resolver: yupResolver(schema),
    context: { fssaiLogoPresent: provideDefaultValues().fssaiLogoPresent },
  });

  useEffect(() => {
    reset(provideDefaultValues(orderDetails?.sampleVariant));
    resetAcceptanceForm(provideDefaultValuesAcceptanceForm());
  }, [
    reset,
    resetAcceptanceForm,
    orderDetails,
    sampleDetails,
    provideDefaultValues,
    provideDefaultValuesAcceptanceForm,
  ]);

  useEffect(() => {
    if (error) {
      notification.error({
        message: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1000);
    }
  }, [dispatch, error]);

  const provideSampleReceivedDetailsToSave = (labSampleReceivedDetails) => {
    let data = {
      ...labSampleReceivedDetails,
      orderId: orderDetails.orderId,
      sampleReceivedDate: dateTimeToString(
        labSampleReceivedDetails.sampleReceivedDate
      ),
      expectedDateOfCompletion: dateToString(
        labSampleReceivedDetails.expectedDateOfCompletion
      ),
    };
    if (orderDetails && orderDetails.sampleVariant !== "Loose") {
      data = {
        ...data,
        manufacturingDate: dateToString(
          labSampleReceivedDetails.manufacturingDate
        ),
        expireByDate: dateToString(labSampleReceivedDetails.expireByDate),
      };
    } else {
      data = {
        ...data,
        //no date comes for loose variant for these fields
        manufacturingDate: "",
        expireByDate: "",
      };
    }
    return data;
  };

  const navigate = (updateOrder, from) => {
    const underDispute = updateOrder?.underDispute?.at(-1);
    if (underDispute?.isResolved) {
      dispatch(orderListActions.setActiveNavOption("REPORT UPLOADED"));
      history.push(reverse(`${routes.labs}`));
    } else if (from === "labellingForm") {
      setOpenLabellingFormSuccessModal(true);
    }
  };

  const handleAcceptanceForm = (data) => {
    const postData = {
      orderId: orderDetails.orderId,
      sampleReceivedDate: data.sampleReceivedDate,
      samplePhysicalCondition: data.samplePhysicalCondition,
      sampleSealNumber: data.sampleSealNumber,
      conditionOfSeal: data.conditionOfSeal,
      additionalSampleNeeded: data.additionalSampleNeeded,
      additionalSampleReasons: data.additionalSampleReasons,
      sampleRegistrationNumber: data.sampleRegistrationNumber,
      expectedDateOfCompletion: data.expectedDateOfCompletion,
    };
    setIsSavingAcceptanceForm(true);
    dispatch(
      actions.saveAcceptanceFormDetails({
        data: postData,
        orderStatus: orderStatusValue,
        onSuccess: (data) => {
          notification.success({
            message: "Sample Acceptance Form Saved Successfully!!!",
          });
          setIsSavingAcceptanceForm(false);
          if (orderStatusValue === "deliveredToLab" || orderStatusValue === "outForPickup") {
            setOpenAcceptanceFormSuccessModal(true);
          } else if (orderStatusValue === "underDispute") {
            navigate(data?.orderDetails);
          }
        },
        onFailure: () => {
          notification.error({
            message: "Failed to save Sample Acceptance Form",
          });
          setIsSavingAcceptanceForm(false);
        },
      })
    );
  };

  const handleLabellingForm = (data) => {
    const formData = {
      orderId: orderDetails.orderId,
      labelingPhotos: [
        data.frontImage?.[0]?.url,
        data.backImage?.[0]?.url,
      ].filter(Boolean), // removes undefined/null

      fssaiLogoPresent: data.fssaiLogoPresent,
      fssaiLicenseNumberPresent: data.fssaiLicenseNumberPresent,
      fssaiLicenseNumber: data.fssaiLicenseNumber,
      brandNamePresent: data.brandNamePresent,
      brandName: data.brandName,

      manufacturingDatePresent: data.manufacturingDatePresent,
      manufacturingDate: data.manufacturingDate,
      expireByDatePresent: data.expireByDatePresent,
      expireByDate: data.expireByDate,

      lotNumberPresent: data.lotNumberPresent,
      lotNumber: data.lotNumber,
      vegSymbolPresent: data.vegSymbolPresent,
      vegOrNonVeg: data.vegOrNonVeg,
      countryOfOriginPresent: data.countryOfOriginPresent,
      countryOfOrigin: data.countryOfOrigin,
      jaivikKisanLogoPresent: data.jaivikKisanLogoPresent,
      manufacturingAddressPresent: data.manufacturingAddressPresent,
      manufacturingAddress: data.manufacturingAddress,
    };

    setIsSavingLabellingForm(true);
    dispatch(
      actions.saveLabellingFormDetails({
        data: formData,
        orderStatus: orderStatusValue,
        onSuccess: (data) => {
          setIsSavingLabellingForm(false);
          if (orderStatusValue === "underDispute"){
            navigate(data?.orderDetails, "labellingForm");
          } else {
            setOpenLabellingFormSuccessModal(true);
          }
        },
        onFailure: () => {
          notification.error({
            message: "Failed to save Sample Labelling Form",
          });
          setIsSavingLabellingForm(false);
        },
      })
    );
  };

  const isAcceptanceFormEditable =
    orderStatusValue === "outForPickup" ||
    orderStatusValue === "deliveredToLab" ||
    orderStatusValue === "receivedAtLab" ||
    orderStatusValue === "underDispute";

  const isLabellingFormEditable =
    orderStatusValue === "receivedAtLab" ||
    orderStatusValue === "underTesting" ||
    orderStatusValue === "reportUploaded" ||
    orderStatusValue === "underDispute";

  const requestCorrections =
    orderDetails?.underDispute
      ?.at(-1)
      ?.requestCorrections?.map(
        (requestCorrection) => requestCorrection.type
      ) || [];
      
  return (
    <>
      {" "}
      <PageHeader
        ghost={false}
        title={`${
          !orderDetails?.sampleReceivedByLabDetails ? "Save" : "Update"
        } Sample Reception Form`}
        subTitle={
          orderDetails
            ? `${orderDetails.orderId},  ${orderDetails.sampleVariant} Variant`
            : ""
        }
        extra={[
          <Button
            key="1"
            onClick={() => {
              history.goBack();
            }}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <MdKeyboardBackspace size={16} style={{ marginRight: 4 }} />
            Back
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh) - 50px",
          marginTop: "30px",
          display: "flex",
          flexDirection: "column",
          gap: "30px",
        }}
      >
        {loading ? (
          <Spin
            size="large"
            style={{
              height: "calc(70vh)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#fff",
            }}
          />
        ) : (
          <>
            {orderStatusValue !== "underDispute" ||
            requestCorrections?.includes("acceptanceForm") ? (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ marginBottom: "25px" }}>
                    Sample Acceptance Form
                  </h2>
                  {isAcceptanceFormEditable ? (
                    <Button
                      type="primary"
                      onClick={handleSubmitAcceptanceForm(handleAcceptanceForm)}
                      loading={isSavingAcceptanceForm}
                    >
                      Save
                    </Button>
                  ) : null}
                </div>
                {/* Acceptance Form  */}
                <AddLabSampleReceptionForm
                  control={acceptanceFormControl}
                  watch={watchAcceptanceForm}
                  errors={acceptanceFormErrors}
                  setValue={acceptanceFormSetValue}
                  disableFields={!isAcceptanceFormEditable}
                />
              </div>
            ) : (
              <></>
            )}

            {[
              "receivedAtLab",
              "underTesting",
              "reportUploaded",
              "completed",
            ].includes(orderStatusValue) ||
            requestCorrections?.includes("labellingForm") ? (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ marginBottom: "25px" }}>
                    Sample Labelling Form
                  </h2>
                  {isLabellingFormEditable ? (
                    <Button
                      type="primary"
                      onClick={handleSubmit(handleLabellingForm)}
                      loading={isSavingLabellingForm}
                    >
                      Save
                    </Button>
                  ) : null}
                </div>
                <AddLabLabellingForm
                  control={control}
                  watch={watch}
                  errors={errors}
                  setValue={setValue}
                  options={{
                    disableFields,
                    disabledFormFields: !isLabellingFormEditable,
                    sampleVariant: orderDetails?.sampleVariant,
                  }}
                />
              </div>
            ) : (
              <></>
            )}
          </>
        )}
      </div>
      <AcceptanceFormSuccessModal
        open={openAcceptanceFormSuccessModal}
        setOpen={setOpenAcceptanceFormSuccessModal}
        id={id}
      />
      <LabellingFormSuccessModal
        open={openLabellingFormSuccessModal}
        setOpen={setOpenLabellingFormSuccessModal}
        id={id}
        getOrderStatusId={getOrderStatusId}
        orderDetails={orderDetails}
      />
    </>
  );
};

export default SampleReceived;
